import { types, flow, getSnapshot, getPath, destroy } from "mobx-state-tree";
import { isEqual, isArray } from "underscore";

import { getPatchProps, filterUndefinedProps } from "util/mst-utils";
import { getStreams } from "api/streams";

import BaseBackboneCollectionMigrationStore from "pages/common/state/store/backbone-collection-template";

import { StreamStore } from "./stream-store";

const model = {
    streams: types.optional(types.array(StreamStore), []),
    selectedStreams: types.array(
        types.safeReference(StreamStore, { acceptsUndefined: false })
    ),
    isLoading: true
};

const volatile = () => ({});

const views = (self) => ({
    get length() {
        return self.streams.length;
    },

    getStreamsAsRawObjects() {
        return self.streams.map((model) => getSnapshot(model));
    },

    getStreamById(streamId) {
        return self.streams.find((s) => s.streamId === streamId);
    },

    getStreamByPatchId(id) {
        return self.streams.find((stream) => {
            const path = getPath(stream);
            const parts = path.split(`/`);
            const patchId = isArray(parts) ? parts[parts.length - 1] : ``;
            return patchId === id;
        });
    },

    getModelIdFromPatch(patch) {
        const props = getPatchProps(patch);
        const stream = self.getStreamByPatchId(props.patchId);
        return stream.streamId;
    }
});

const actions = (self) => ({
    /**
     * @summary Resets entire streams list with new StreamStore models
     * @param {Array} streams
     */
    resetStreams(streams) {
        const sortedStreams = self.sortByDisplayName(streams);
        self.streams = sortedStreams.map((stream) => {
            return StreamStore.create(stream);
        });
    },

    /**
     * @summary Updates the model of a specific stream
     * @param {Object} data
     */
    resetStream(data) {
        const model = self.getStreamById(data.streamId);
        model?.reset(data) || self.addStream(data);
    },

    /**
     * @summary Compares new data with existing streams and only updates
     * models that are changed, deleted or added
     * @param {Array} data - list of stream objects
     * @param {Object} _options - Boolean properties which enable/disable certain actions
     */
    addStreams(data, _options = {}) {
        const defaultOptions = {
            add: true,
            remove: true,
            update: true
        };
        const options = { ...defaultOptions, ..._options };
        const touched = [];

        if (options.remove) {
            const streamIds = data.map((s) => s.streamId);
            const deletedModels = self.streams.filter(
                (model) => !streamIds.includes(model.streamId)
            );
            deletedModels.forEach((model) => model.destroy());
        }

        data.forEach((stream) => {
            const model = self.getStreamById(stream.streamId);
            if (model) {
                if (options.update) {
                    model.update(stream);
                    touched.push(model);
                }
            } else {
                if (options.add) {
                    self.addStream(stream);
                    touched.push(model);
                }
            }
        });
        return touched;
    },

    /**
     * @summary Add a new stream to the streams list (in the correct sort position)
     * @param {Object} data
     */
    addStream(data) {
        const sortedData = self.sortByDisplayName([
            ...self.getStreamsAsRawObjects(),
            data
        ]);
        const itemPos = sortedData.findIndex(
            (item) => item.streamId === data.streamId
        );
        const stream = StreamStore.create(data);
        self.streams.splice(itemPos, 0, stream);
    },

    /**
     * @summary Remove a stream from the streams list
     * @param {StreamStore | String} stream
     */
    removeStream(stream) {
        let node = stream;
        if (typeof stream === `string`) {
            node = self.getStreamById(stream);
        }
        destroy(node);
    },

    setSelectedStreams(streams) {
        self.selectedStreams = streams;
    },

    sortByDisplayName(streams) {
        return streams.sort((s1, s2) => {
            return `${s1.displayName}`.localeCompare(s2.displayName);
        });
    },

    sort() {
        self.streams.sort((s1, s2) => {
            return `${s1.displayName}`.localeCompare(s2.displayName);
        });
    },

    hasChanged(data) {
        const stream = self.getStreamById(data?.streamId);
        const snapshot = filterUndefinedProps(getSnapshot(stream));
        return !isEqual(snapshot, data);
    }
});

const asyncActions = (self) => ({
    /**
     * @summary Make an api call to fetch all streams
     * @async
     * @return {StreamObject | Error}
     */
    fetch: flow(function* fetch() {
        self.isLoading = true;
        try {
            const { data } = yield getStreams();
            self.addStreams(data);
            const streams = self.getStreamsAsRawObjects();
            return streams;
        } catch (error) {
            console.error(error);
            return error;
        } finally {
            self.isLoading = false;
        }
    })
});

const backboneUtilityActions = () => ({
    __comparator(model1, model2) {
        return `${model1.get(`displayName`)}`.localeCompare(
            model2.get(`displayName`)
        );
    }
});

const _StreamsStore = types
    .model(`Streams`, model)
    .volatile(volatile)
    .views(views)
    .actions(actions)
    .actions(asyncActions)
    .actions(backboneUtilityActions);

export const StreamsStore = BaseBackboneCollectionMigrationStore(
    _StreamsStore,
    StreamStore,
    `streams`
);
